import React, { useEffect, useMemo, useState } from 'react';

/**
 * VenueToggleDrawer – a bottom-tray component that shows one toggle per venue.
 *
 * Each venue maps to one or more transit routes via its `cooresponding_route`
 * field (comma-separated string). Toggling a venue on/off shows/hides all of
 * its associated routes on the map.
 *
 * Props:
 *  - venues:            Array of point_data objects from static-content.json
 *  - activeVenueIds:    Set<string> of currently-active venue ids
 *  - onToggleVenue:     (venueId: string) => void
 *  - maxHeight:         number (px) – drawer max height
 *  - mapRef:            ref to the Leaflet map instance
 *  - onOpenPointPopup:  callback to open a venue's popup on the map
 *  - trayConfig:        staticOverlays.ui.tray from campaign-config.json
 */
const VenueToggleDrawer = ({
  venues,
  activeVenueIds,
  onToggleVenue,
  maxHeight = 200,
  mapRef,
  onOpenPointPopup,
  trayConfig
}) => {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const safeVenues = useMemo(() => {
    return Array.isArray(venues) ? venues.filter(v => v && v.id) : [];
  }, [venues]);

  const isActive = (venueId) => {
    if (!activeVenueIds) return true;
    return activeVenueIds.has(String(venueId));
  };

  const onLoadMessage = (trayConfig && typeof trayConfig.on_load_message === 'string')
    ? trayConfig.on_load_message
    : '';
  const onLoadIcon = (trayConfig && typeof trayConfig.on_load_icon === 'string')
    ? trayConfig.on_load_icon
    : '';
  const showIcon = Boolean(trayConfig && trayConfig.showIcon);

  const handleVenueClick = (venue) => {
    if (!venue || !mapRef || !mapRef.current) return;
    const lat = Number(venue.lat);
    const lng = Number(venue.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    mapRef.current.setView([lat, lng], 14, { animate: true, duration: 0.5 });

    if (typeof onOpenPointPopup === 'function') {
      window.setTimeout(() => onOpenPointPopup(venue), 350);
    }
  };

  const VENUE_COLOR = '#0d6efd';

  return (
    <div id="route-status-drawer" style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderTop: '1px solid #ddd',
      padding: '12px',
      zIndex: 1000,
      maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
      overflowY: 'auto'
    }}>
      {/* Header message */}
      {onLoadMessage && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          paddingBottom: '10px',
          marginBottom: '10px',
          borderBottom: '1px solid #eee'
        }}>
          {showIcon && onLoadIcon && (
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '14px',
              background: 'white',
              border: '1px solid #eee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              lineHeight: 1,
              flex: '0 0 28px'
            }}>
              {onLoadIcon}
            </div>
          )}
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#333',
            textAlign: 'center'
          }}>
            {onLoadMessage}
          </div>
        </div>
      )}

      {/* Venue toggles */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: safeVenues.length <= 3
          ? `repeat(${safeVenues.length}, minmax(0, 1fr))`
          : (viewportWidth >= 992
            ? 'repeat(3, minmax(0, 1fr))'
            : 'repeat(auto-fit, minmax(140px, 1fr))'),
        gap: '6px'
      }}>
        {safeVenues.map((venue) => {
          const active = isActive(venue.id);
          const emoji = typeof venue['icon-emoji'] === 'string' ? venue['icon-emoji'].trim() : '';

          return (
            <div
              key={venue.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 8px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #eee',
                fontSize: '11px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: active ? 1 : 0.65
              }}
              title={active ? 'Click to hide routes for this venue' : 'Click to show routes for this venue'}
              onClick={() => handleVenueClick(venue)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', flex: 1 }}>
                {emoji && (
                  <span style={{ fontSize: '16px', lineHeight: 1, flex: '0 0 auto' }}>{emoji}</span>
                )}
                <span style={{
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: '13px',
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {venue.title || venue.id}
                </span>
              </div>

              <label
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '36px',
                  height: '20px',
                  flex: '0 0 36px',
                  marginLeft: '8px'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  role="switch"
                  checked={active}
                  onChange={() => {
                    if (onToggleVenue) onToggleVenue(String(venue.id));
                  }}
                  style={{
                    opacity: 0,
                    width: 0,
                    height: 0,
                    position: 'absolute'
                  }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: active ? VENUE_COLOR : '#ccc',
                    transition: '0.2s',
                    borderRadius: '999px'
                  }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    height: '16px',
                    width: '16px',
                    left: active ? '18px' : '2px',
                    top: '2px',
                    backgroundColor: 'white',
                    transition: '0.2s',
                    borderRadius: '50%',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}
                />
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VenueToggleDrawer;